import React, { useMemo, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import Icon from './Icon';
import useAdminMe from '../../hooks/useAdminMe';
import './AdminSidebar.css';

// Admin IA & RBAC — one taxonomy, each item tagged with the `perm` its route
// enforces, so "not in nav" ⟺ "403 on route". Items with no `perm` are visible
// to every admin (Dashboard). Exported for the mobile tabs + the no-gap test.
export const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { to: '/admin', icon: 'dashboard', label: 'Dashboard', end: true },
    ],
  },
  {
    label: 'Sell',
    items: [
      { to: '/admin/pos',       icon: 'cart',      label: 'POS register', perm: 'orders' },
      { to: '/admin/orders',    icon: 'cart',      label: 'Orders',       perm: 'orders' },
      { to: '/admin/returns',   icon: 'rotate',    label: 'Returns',      perm: 'orders' },
      { to: '/admin/quotes',    icon: 'file',      label: 'Quotes / RFQ', perm: 'orders' },
      { to: '/admin/buylist',   icon: 'tag',       label: 'Buylist',      perm: 'orders' },
      { to: '/admin/wholesale', icon: 'briefcase', label: 'Wholesale',    perm: 'orders' },
    ],
  },
  {
    label: 'Catalog',
    items: [
      { to: '/admin/products',             icon: 'box',   label: 'Products',            perm: 'products' },
      { to: '/admin/sets',                 icon: 'box',   label: 'Singles inventory (sets)', perm: 'products' },
      { to: '/admin/categories',           icon: 'tag',   label: 'Categories (sets + tags)', perm: 'products' },
      { to: '/admin/sets/condition-rules', icon: 'tag',   label: 'Condition rules',     perm: 'products' },
      { to: '/admin/enrichment-queue',     icon: 'tag',   label: 'Enrichment queue',    perm: 'products' },
      { to: '/admin/import',               icon: 'upload',label: 'Bulk import',         perm: 'products' },
      { to: '/admin/catalog/maintenance',  icon: 'trash', label: 'Catalog maintenance', perm: 'products' },
      { to: '/admin/drops',                icon: 'calendar', label: 'Drops',            perm: 'products' },
      { to: '/admin/reviews',              icon: 'star',  label: 'Reviews',             perm: 'products' },
    ],
  },
  {
    label: 'Inventory & Fulfillment',
    items: [
      { to: '/admin/inventory',      icon: 'archive',       label: 'Inventory',      perm: 'inventory' },
      { to: '/admin/stock-forecast', icon: 'barChart',      label: 'Stock forecast', perm: 'inventory' },
      { to: '/admin/scan',           icon: 'scan',          label: 'Scan mode',      perm: 'inventory' },
      { to: '/admin/receiving',      icon: 'truck',         label: 'Receiving',      perm: 'inventory' },
      { to: '/admin/counts',         icon: 'clipboard',     label: 'Cycle counts',   perm: 'inventory' },
      { to: '/admin/vendors',        icon: 'briefcase',     label: 'Vendors',        perm: 'inventory' },
      { to: '/admin/writeoffs',      icon: 'alertTriangle', label: 'Write-offs',     perm: 'inventory' },
      { to: '/admin/warehouses',     icon: 'box',           label: 'Warehouses',     perm: 'inventory' },
      { to: '/admin/routing-rules',  icon: 'box',           label: 'Routing rules',  perm: 'inventory' },
    ],
  },
  {
    label: 'Pricing',
    items: [
      { to: '/admin/markup-rules',    icon: 'tag', label: 'Markup rules',    perm: 'products' },
      { to: '/admin/pricing',         icon: 'tag', label: 'Pricing rules',   perm: 'products' },
      { to: '/admin/price-lists',     icon: 'tag', label: 'Price lists (B2B)', perm: 'products' },
      { to: '/admin/purchase-limits', icon: 'shield', label: 'Purchase limits', perm: 'products' },
    ],
  },
  {
    label: 'AI Grading',
    items: [
      { to: '/admin/grading', icon: 'image', label: 'AI Grading', perm: 'products' },
    ],
  },
  {
    label: 'Customers',
    items: [
      { to: '/admin/customers',       icon: 'users',  label: 'Customers',        perm: 'users' },
      { to: '/admin/customer-groups', icon: 'users',  label: 'Customer groups',  perm: 'users' },
      { to: '/admin/applications',    icon: 'users',  label: 'Account applications', perm: 'users' },
      { to: '/admin/loyalty',         icon: 'star',   label: 'Loyalty tiers',    perm: 'marketing' },
      { to: '/admin/referrals',       icon: 'users',  label: 'Referrals',        perm: 'marketing' },
      { to: '/admin/subscriptions',   icon: 'repeat', label: 'Subscriptions',    perm: 'marketing' },
      // Discoverability: staff/admin logins live under Platform → Team, but users
      // look for "user management" here. perm:'platform' = only super_admin sees it.
      { to: '/admin/team',            icon: 'shield', label: 'Admin users →',    perm: 'platform' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { to: '/admin/coupons',          icon: 'tag',  label: 'Coupons',          perm: 'marketing' },
      { to: '/admin/newsletter',       icon: 'mail', label: 'Newsletter',       perm: 'marketing' },
      { to: '/admin/automations',      icon: 'zap',  label: 'Automations',      perm: 'marketing' },
      { to: '/admin/automation-flows', icon: 'zap',  label: 'Automation flows', perm: 'marketing' },
      { to: '/admin/funnels',          icon: 'zap',  label: 'Funnels',          perm: 'marketing' },
      { to: '/admin/exit-intent',      icon: 'zap',  label: 'Exit-intent recovery', perm: 'marketing' },
      { to: '/admin/bundles',          icon: 'package', label: 'Bundles & upsells', perm: 'marketing' },
      { to: '/admin/posts',            icon: 'fileText', label: 'Blog posts',    perm: 'marketing' },
      { to: '/admin/chatbot',          icon: 'mail', label: 'Chatbot & leads',  perm: 'marketing' },
      { to: '/admin/alerts',           icon: 'bell', label: 'Alert subscribers', perm: 'marketing' },
      { to: '/admin/queue-alerts',     icon: 'bell', label: 'Queue alerts',     perm: 'marketing' },
    ],
  },
  {
    label: 'Channels',
    items: [
      { to: '/admin/channels/tcgplayer', icon: 'tag', label: 'TCGplayer', perm: 'analytics' },
      { to: '/admin/channels/ebay',      icon: 'tag', label: 'eBay',      perm: 'analytics' },
      { to: '/admin/channels/amazon',    icon: 'tag', label: 'Amazon',    perm: 'analytics' },
      { to: '/admin/channels/tiktok',    icon: 'tag', label: 'TikTok Shop', perm: 'analytics' },
    ],
  },
  {
    label: 'Content & SEO',
    items: [
      { to: '/admin/builder',       icon: 'image', label: 'Page Builder', perm: 'analytics' },
      { to: '/admin/seo',           icon: 'globe', label: 'SEO',          perm: 'analytics' },
      { to: '/admin/seo-content',   icon: 'globe', label: 'SEO content',  perm: 'analytics' },
      { to: '/admin/press-gallery', icon: 'image', label: 'Press gallery', perm: 'analytics' },
      { to: '/admin/flyers',        icon: 'image', label: 'Flyers',       perm: 'analytics' },
      { to: '/admin/banners',       icon: 'image', label: 'Hero banners (legacy)', perm: 'analytics' },
      { to: '/admin/collections',   icon: 'package', label: 'Collections (flagged)', perm: 'analytics' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/admin/accounting', icon: 'calculator', label: 'Accounting',          perm: 'analytics' },
      { to: '/admin/invoicing',  icon: 'filePdf',    label: 'Invoicing',           perm: 'analytics' },
      { to: '/admin/ar',         icon: 'tag',        label: 'Accounts receivable', perm: 'analytics' },
      { to: '/admin/reports',    icon: 'barChart',   label: 'Reports',             perm: 'analytics' },
    ],
  },
  {
    label: 'Store Settings',
    items: [
      { to: '/admin/site-settings', icon: 'settings', label: 'Site settings', perm: 'analytics' },
      { to: '/admin/email-roles',   icon: 'mail',     label: 'Email roles',   perm: 'analytics' },
      { to: '/admin/email-log',     icon: 'mail',     label: 'Email log',     perm: 'analytics' },
      { to: '/admin/emails',        icon: 'mail',     label: 'Emails (preview)', perm: 'analytics' },
      { to: '/admin/pwa',           icon: 'bell',     label: 'PWA & push',    perm: 'analytics' },
      { to: '/admin/search-analytics', icon: 'search', label: 'Search analytics', perm: 'analytics' },
    ],
  },
  {
    label: 'Platform',
    items: [
      { to: '/admin/sites',            icon: 'globe',  label: 'Sites (multi-tenant)', perm: 'platform' },
      { to: '/admin/team',             icon: 'shield', label: 'Team',          perm: 'platform' },
      { to: '/admin/integrations',     icon: 'plug',   label: 'Integrations',  perm: 'platform' },
      { to: '/admin/helcim',           icon: 'plug',   label: 'Helcim',        perm: 'platform' },
      { to: '/admin/zoho-setup',       icon: 'plug',   label: 'Zoho — setup',  perm: 'platform' },
      { to: '/admin/zoho/mapping',     icon: 'plug',   label: 'Zoho mapping',  perm: 'platform' },
      { to: '/admin/zoho/sync-log',    icon: 'zap',    label: 'Zoho sync log', perm: 'platform' },
      { to: '/admin/api-tokens',       icon: 'plug',   label: 'API tokens',    perm: 'platform' },
      { to: '/admin/feature-flags',    icon: 'toggle', label: 'Feature flags', perm: 'platform' },
      { to: '/admin/webhooks',         icon: 'plug',   label: 'Webhook events', perm: 'platform' },
      { to: '/admin/jobs',             icon: 'zap',    label: 'Job inspector', perm: 'platform' },
      { to: '/admin/audit',            icon: 'bookOpen', label: 'Audit log',   perm: 'platform' },
      { to: '/admin/errors',           icon: 'alertTriangle', label: 'Error log', perm: 'platform' },
      { to: '/admin/launch-readiness', icon: 'check',  label: 'Launch readiness', perm: 'platform' },
    ],
  },
];

// The permission a given admin path requires (longest matching nav `to`
// prefix). Returns undefined when no gate applies (e.g. Dashboard / unknown
// detail pages) — callers treat undefined as "allowed".
export function permForPath(pathname) {
  let best = null;
  for (const group of NAV_GROUPS) {
    for (const it of group.items) {
      if (pathname === it.to || pathname.startsWith(it.to + '/')) {
        if (!best || it.to.length > best.to.length) best = it;
      }
    }
  }
  return best ? best.perm : undefined;
}

// Visible nav for a given can() predicate, optionally filtered by a search term.
export function visibleGroups(can, query = '') {
  const q = query.trim().toLowerCase();
  return NAV_GROUPS
    .map(group => ({
      ...group,
      items: group.items.filter(it =>
        can(it.perm) && (!q || it.label.toLowerCase().includes(q))),
    }))
    .filter(group => group.items.length > 0);
}

export default function AdminSidebar({ open, onClose }) {
  const { can } = useAdminMe();
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_nav_collapsed') || '{}'); } catch { return {}; }
  });

  // `can` already changes whenever role/permissions change, so it covers role.
  const groups = useMemo(() => visibleGroups(can, query), [can, query]);

  const toggleGroup = (label) => {
    setCollapsed(prev => {
      const next = { ...prev, [label]: !prev[label] };
      try { localStorage.setItem('admin_nav_collapsed', JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };
  const searching = query.trim().length > 0;

  return (
    <>
      {open && <div className="admin-sidebar__backdrop" onClick={onClose} />}
      <aside className={`admin-sidebar${open ? ' admin-sidebar--open' : ''}`} aria-label="Admin navigation">
        <div className="admin-sidebar__header">
          <Link to="/admin" className="admin-sidebar__brand" onClick={onClose}>
            SilverGames
          </Link>
        </div>

        <div className="admin-sidebar__search">
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search admin…"
            aria-label="Search admin navigation"
            className="admin-sidebar__search-input"
          />
        </div>

        <nav className="admin-sidebar__nav">
          {groups.map(group => {
            const isCollapsed = !searching && !!collapsed[group.label];
            return (
              <div key={group.label} className="admin-sidebar__group">
                <button
                  type="button"
                  className="admin-sidebar__group-label admin-sidebar__group-toggle"
                  aria-expanded={!isCollapsed}
                  onClick={() => toggleGroup(group.label)}
                >
                  {group.label}
                  <span aria-hidden="true" className="admin-sidebar__chevron">{isCollapsed ? '▸' : '▾'}</span>
                </button>
                {!isCollapsed && (
                  <ul className="admin-sidebar__list">
                    {group.items.map(item => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          className={({ isActive }) => 'admin-sidebar__link' + (isActive ? ' admin-sidebar__link--active' : '')}
                          onClick={onClose}
                        >
                          <Icon name={item.icon} size={16} className="admin-sidebar__link-icon" />
                          <span>{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
          {searching && groups.length === 0 && (
            <div className="admin-sidebar__empty">No matches for “{query}”.</div>
          )}
        </nav>

        <div className="admin-sidebar__footer">
          <Link to="/" className="admin-sidebar__link" onClick={onClose}>
            <Icon name="storefront" size={16} className="admin-sidebar__link-icon" />
            <span>View storefront</span>
          </Link>
        </div>
      </aside>
    </>
  );
}
